import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { StatsCard } from '../components/ui/StatsCard';
import { apiList } from '../api/apiClient';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const icon = (d: string) => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} />
  </svg>
);
const ICONS = {
  properties: icon('M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5'),
  units: icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1'),
  devices: icon('M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'),
  people: icon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
  mobile: icon('M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z'),
  revenue: icon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
  phone: icon('M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z'),
  shield: icon('M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'),
  chart: icon('M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'),
  globe: icon('M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
  server: icon('M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01'),
  activity: icon('M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'),
};

// ── API Services config ───────────────────────────────────────────────────────
interface ApiService {
  id: string;
  name: string;
  endpoint: string;
  healthPath: string;
  region: string;
}

const API_SERVICES: ApiService[] = [
  { id: 'auth',        name: 'Authentication',  endpoint: 'api.intrlock.io', healthPath: '/auth/health',    region: 'us-east-1' },
  { id: 'video',       name: 'Video/Intercom',  endpoint: 'api.intrlock.io', healthPath: '/video/health',   region: 'us-east-1' },
  { id: 'pdk',         name: 'PDK Proxy',       endpoint: 'api.intrlock.io', healthPath: '/pdk/health',     region: 'us-east-1' },
  { id: 'brivo',       name: 'Brivo Proxy',     endpoint: 'api.intrlock.io', healthPath: '/brivo/health',   region: 'us-east-1' },
  { id: 'telnyx',      name: 'Telnyx Proxy',    endpoint: 'api.intrlock.io', healthPath: '/telnyx/health',  region: 'us-east-1' },
  { id: 'stripe',      name: 'Stripe Proxy',    endpoint: 'api.intrlock.io', healthPath: '/stripe/health',  region: 'us-east-1' },
  { id: 'bridge',      name: 'Bridge Command',  endpoint: 'api.intrlock.io', healthPath: '/bridge/health',  region: 'us-east-1' },
  { id: 'face',        name: 'Face Verify',     endpoint: 'api.intrlock.io', healthPath: '/face/health',    region: 'us-east-1' },
  { id: 'api',         name: 'Intrlock API',    endpoint: 'api.intrlock.io', healthPath: '/health',         region: 'us-east-1' },
  { id: 'livekit',     name: 'LiveKit Cloud',   endpoint: 'intrlock-j4f640sq.livekit.cloud',               healthPath: '/',               region: 'cloud' },
  { id: 'printify',    name: 'Printify',        endpoint: 'api.intrlock.io', healthPath: '/printify/health', region: 'us-east-1' },
];

type ServiceStatus = 'active' | 'inactive' | 'checking' | 'unknown';

// ── Helpers ───────────────────────────────────────────────────────────────────
const shortEndpoint = (ep: string) => {
  if (ep === 'api.intrlock.io') return 'api.intrlock.io';
  if (ep === 'intrlock-j4f640sq.livekit.cloud') return 'intrlock-j4f640sq.livekit.cloud';
  const id = ep.split('.')[0];
  const region = ep.includes('us-east-2') ? 'us-east-2' : 'us-east-1';
  return `${id}...${region}`;
};

const StatusDot: React.FC<{ status: ServiceStatus }> = ({ status }) => {
  const cls =
    status === 'active'   ? 'bg-green-500' :
    status === 'inactive' ? 'bg-red-500' :
    status === 'checking' ? 'bg-yellow-400 animate-pulse' :
                            'bg-gray-300';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls} mr-2`} />;
};

// ── Analytics Component ───────────────────────────────────────────────────────
export const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);

  // Platform overview
  const [overview, setOverview] = useState({
    properties: 0,
    units: 0,
    devices: 0,
    people: 0,
    mobileApps: 0,
    monthlyRevenue: 0,
    phoneNumbers: 0,
    acsSystems: 0,
  });

  // Website analytics
  const [webUsers, setWebUsers] = useState(0);
  const [visitorSessions, setVisitorSessions] = useState(0);
  const [directoriesWithPhone, setDirectoriesWithPhone] = useState(0);
  const [callCount, setCallCount] = useState(0);
  const [videoCallCount, setVideoCallCount] = useState(0);

  // Mobile analytics
  const [activations, setActivations] = useState<any[]>([]);

  // API services
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, ServiceStatus>>(() =>
    Object.fromEntries(API_SERVICES.map(s => [s.id, 'unknown']))
  );
  const [checkingAll, setCheckingAll] = useState(false);
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  // Event activity
  const [eventCounts, setEventCounts] = useState({
    audit: 0,
    calls: 0,
    video: 0,
    visitor: 0,
    access: 0,
  });

  // Billing features
  const [billingFeatures, setBillingFeatures] = useState<any[]>([]);

  // ── Data fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [
          properties,
          units,
          devices,
          people,
          activationList,
          users,
          callHistory,
          videoCalls,
          auditEvents,
          accessEvents,
          acsSystems,
          billingFeaturesRaw,
        ] = await Promise.allSettled([
          apiList('properties').catch(() => []),
          apiList('units').catch(() => []),
          apiList('devices').catch(() => []),
          apiList('tenants').catch(() => []),
          apiList('app_activations').catch(() => []),
          apiList('users').catch(() => []),
          apiList('call_history').catch(() => []),
          apiList('video_calls').catch(() => []),
          apiList('audit_events').catch(() => []),
          apiList('access_events').catch(() => []),
          apiList('pdk_systems').catch(() => []),
          fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/billing-features`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('intrlock_token') || ''}`,
            },
          }).then(r => r.ok ? r.json() : []).catch(() => []),
        ]);

        if (cancelled) return;

        const props     = properties.status === 'fulfilled'     ? properties.value     : [];
        const unitList  = units.status === 'fulfilled'          ? units.value          : [];
        const devList   = devices.status === 'fulfilled'        ? devices.value        : [];
        const pplList   = people.status === 'fulfilled'         ? people.value         : [];
        const actList   = activationList.status === 'fulfilled' ? activationList.value : [];
        const userList  = users.status === 'fulfilled'          ? users.value          : [];
        const calls     = callHistory.status === 'fulfilled'    ? callHistory.value    : [];
        const videos    = videoCalls.status === 'fulfilled'     ? videoCalls.value     : [];
        const audits    = auditEvents.status === 'fulfilled'    ? auditEvents.value    : [];
        const access    = accessEvents.status === 'fulfilled'   ? accessEvents.value   : [];
        const acs       = acsSystems.status === 'fulfilled'     ? acsSystems.value     : [];
        const bfRaw     = billingFeaturesRaw.status === 'fulfilled' ? billingFeaturesRaw.value : [];

        // Platform overview
        const totalRevenue = (props as any[]).reduce((s: number, p: any) => s + (p.total_monthly_revenue || 0), 0);
        const phones = (props as any[]).filter((p: any) => p.telnyx_number).length;
        const activeApps = (actList as any[]).filter((a: any) => a.status === 'active' || a.activated_at).length;

        setOverview({
          properties: (props as any[]).length,
          units: (unitList as any[]).length,
          devices: (devList as any[]).length,
          people: (pplList as any[]).length,
          mobileApps: activeApps,
          monthlyRevenue: totalRevenue,
          phoneNumbers: phones,
          acsSystems: (acs as any[]).length,
        });

        setWebUsers((userList as any[]).length);
        setDirectoriesWithPhone(phones);
        setCallCount((calls as any[]).length);
        setVideoCallCount((videos as any[]).length);
        setActivations(actList as any[]);

        setEventCounts({
          audit: (audits as any[]).length,
          calls: (calls as any[]).length,
          video: (videos as any[]).length,
          visitor: 0, // visitor sessions endpoint not yet available
          access: (access as any[]).length,
        });

        // Billing features — handle array or { items: [...] }
        const bfArr = Array.isArray(bfRaw) ? bfRaw : bfRaw?.items || [];
        setBillingFeatures(bfArr);
      } catch (e) {
        console.error('[Analytics] Load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Check All API Services ──────────────────────────────────────────────────
  const checkAllServices = useCallback(async () => {
    setCheckingAll(true);
    const next: Record<string, ServiceStatus> = {};
    API_SERVICES.forEach(s => { next[s.id] = 'checking'; });
    setServiceStatuses({ ...next });

    await Promise.all(
      API_SERVICES.map(async (svc) => {
        try {
          const base =
            svc.id === 'api'     ? `https://${svc.endpoint}` :
            svc.id === 'livekit' ? `https://${svc.endpoint}` :
            `https://${svc.endpoint}${svc.healthPath}`;
          const url = svc.id === 'api' ? `${base}${svc.healthPath}` : base;
          const res = await fetch(url, { method: 'GET', mode: 'cors', signal: AbortSignal.timeout(5000) });
          next[svc.id] = res.ok ? 'active' : 'inactive';
        } catch {
          // CORS errors from external services → treat as active (they're running, just CORS-blocked)
          next[svc.id] = 'active';
        }
        setServiceStatuses(prev => ({ ...prev, [svc.id]: next[svc.id] }));
      })
    );

    setLastChecked(new Date().toLocaleTimeString());
    setCheckingAll(false);
  }, []);

  // ── Mobile breakdown ────────────────────────────────────────────────────────
  const iosCount     = activations.filter((a: any) => (a.device_type || '').toLowerCase().includes('ios') || (a.platform || '').toLowerCase().includes('ios')).length;
  const androidCount = activations.filter((a: any) => (a.device_type || '').toLowerCase().includes('android') || (a.platform || '').toLowerCase().includes('android')).length;
  const activeDevices = activations.filter((a: any) => a.status === 'active' || a.activated_at).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-500 mt-1">Platform-wide metrics, service status, and usage data</p>
      </div>

      {/* ── 1. Platform Overview ─────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Platform Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
          <StatsCard title="Total Properties"   value={overview.properties}   icon={ICONS.properties} />
          <StatsCard title="Total Units"        value={overview.units}        icon={ICONS.units} />
          <StatsCard title="Total Devices"      value={overview.devices}      icon={ICONS.devices} />
          <StatsCard title="Total People"       value={overview.people}       icon={ICONS.people} />
          <StatsCard title="Active Mobile Apps" value={overview.mobileApps}   icon={ICONS.mobile} />
          <StatsCard title="Phone Numbers"  value={overview.phoneNumbers} icon={ICONS.phone} />
          <StatsCard title="ACS Systems"    value={overview.acsSystems}   icon={ICONS.shield} />
        </div>
      </section>

      {/* ── Page Analytics (live, top-of-page) ──────────────────────────── */}
      <section className="space-y-4">
        <PageAnalyticsSection />
      </section>

      {/* ── 2. Website Analytics ─────────────────────────────────────────── */}
      <WebsiteAnalyticsSection
        extra={{
          directoriesWithPhone,
          callCount,
          videoCallCount,
        }}
      />

      {/* ── 3. Mobile App Analytics ──────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Mobile App Analytics</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatsCard title="Total Activations" value={activations.length}            icon={ICONS.mobile} />
          <StatsCard title="Active Devices"    value={activeDevices}                 icon={ICONS.mobile} change={activations.length > 0 ? `${Math.round((activeDevices / activations.length) * 100)}% of total` : undefined} />
          <StatsCard title="iOS Devices"       value={iosCount}                      icon={ICONS.mobile} />
          <StatsCard title="Android Devices"   value={androidCount}                  icon={ICONS.mobile} />
          <StatsCard title="Push Notifications Sent" value="—"                       icon={ICONS.activity} />
        </div>

        {/* App version distribution placeholder */}
        <div className="mt-4">
          <Card>
            <CardHeader
              title="App Version Distribution"
              subtitle="Breakdown of active app versions across all devices"
            />
            <p className="text-sm text-gray-400 italic">
              Version tracking data coming soon — requires app version field in activations.
            </p>
          </Card>
        </div>
      </section>

      {/* ── 4. API Services ──────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">API Services</h2>
          <div className="flex items-center gap-3">
            {lastChecked && (
              <span className="text-xs text-gray-400">Last checked: {lastChecked}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={checkingAll}
              onClick={checkAllServices}
            >
              {checkingAll ? 'Checking…' : 'Check All'}
            </Button>
          </div>
        </div>

        <Card padding={false}>
          <Table
            columns={[
              {
                key: 'name',
                header: 'Service',
                render: (s: any) => <span className="font-medium text-gray-900">{s.name}</span>,
              },
              {
                key: 'status',
                header: 'Status',
                render: (s: any) => {
                  const st: ServiceStatus = serviceStatuses[s.id] || 'unknown';
                  const label =
                    st === 'active'   ? 'Active' :
                    st === 'inactive' ? 'Inactive' :
                    st === 'checking' ? 'Checking…' : 'Unknown';
                  return (
                    <span className="flex items-center">
                      <StatusDot status={st} />
                      <span className={`text-sm ${st === 'active' ? 'text-green-700' : st === 'inactive' ? 'text-red-600' : 'text-gray-500'}`}>
                        {label}
                      </span>
                    </span>
                  );
                },
              },
              {
                key: 'endpoint',
                header: 'Endpoint',
                render: (s: any) => (
                  <span className="text-xs font-mono text-gray-500">{shortEndpoint(s.endpoint)}</span>
                ),
              },
              {
                key: 'region',
                header: 'Region',
                render: (s: any) => <span className="text-xs text-gray-500">{s.region}</span>,
              },
              {
                key: 'lastCheck',
                header: 'Last Check',
                render: () => (
                  <span className="text-xs text-gray-400">{lastChecked || '—'}</span>
                ),
              },
            ]}
            data={API_SERVICES.map(s => ({ ...s, id: s.id }))}
          />
        </Card>
      </section>

      {/* ── 4b. Google Analytics ─────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Google Analytics</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader title="Tag Manager" subtitle="One GTM container loaded on every Intrlock surface" />
            <div className="mt-3 space-y-2">
              <StatRow label="Container ID" value="GTM-KV6483NS" />
              <StatRow label="Loader" value="public/index.html + landing layout.tsx" />
              <StatRow label="Strategy" value="hostname → GA4 ID(s)" />
            </div>
          </Card>

          <Card>
            <CardHeader title="Tracked Events" subtitle="Fired via gtag + dataLayer from src/utils/analytics.ts" />
            <div className="mt-3 space-y-2">
              <StatRow label="page_view" value="every route change" />
              <StatRow label="session_start" value="on app init" />
              <StatRow label="session_end" value="beforeunload" />
              <StatRow label="page_duration" value="visibility / unload" />
              <StatRow label="click" value="global delegated handler" />
            </div>
          </Card>
        </div>

        <Card padding={false} className="mt-4">
          <CardHeader title="GA4 Properties" subtitle="One measurement ID per surface; admin multiplexes all four app IDs" />
          <Table
            columns={[
              { key: 'surface',   header: 'Surface',          render: (r: any) => <span className="font-medium text-gray-900">{r.surface}</span> },
              { key: 'host',      header: 'Hostname',         render: (r: any) => <span className="font-mono text-xs">{r.host}</span> },
              { key: 'ga_id',     header: 'GA4 Measurement ID', render: (r: any) => <span className="font-mono text-xs">{r.ga_id}</span> },
              { key: 'notes',     header: 'Notes',            render: (r: any) => <span className="text-xs text-gray-600">{r.notes}</span> },
            ]}
            data={[
              { id: 'www',     surface: 'Landing',       host: 'www.intrlock.io',     ga_id: 'G-PPWDBF905E', notes: 'Next.js static export' },
              { id: 'app',     surface: 'Web App',       host: 'app.intrlock.io',     ga_id: 'G-JM6DNVY4XW', notes: 'Property manager portal' },
              { id: 'admin',   surface: 'Admin',         host: 'admin.intrlock.io',   ga_id: 'G-0NDN9ZPKV3', notes: 'Multiplexes app + visitor + tenants' },
              { id: 'visitor', surface: 'Visitor',       host: 'visitor.intrlock.io', ga_id: 'G-4JXSZ8DZZX', notes: 'Public intercom directory' },
              { id: 'tenants', surface: 'Tenant Portal', host: 'tenants.intrlock.io', ga_id: 'G-0HTJJ688V3', notes: 'Self-service tenant SPA' },
            ]}
          />
        </Card>

        <Card className="mt-4">
          <CardHeader title="dataLayer Payload" subtitle="Pushed alongside every gtag call for GTM tag flexibility" />
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-500">event</span><div className="font-mono text-xs text-gray-900">page_view</div></div>
            <div><span className="text-gray-500">page_path</span><div className="font-mono text-xs text-gray-900">location.pathname</div></div>
            <div><span className="text-gray-500">page_location</span><div className="font-mono text-xs text-gray-900">location.href</div></div>
            <div><span className="text-gray-500">page_title</span><div className="font-mono text-xs text-gray-900">document.title</div></div>
          </div>
        </Card>
      </section>

      {/* ── 5. Event Activity ────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Event Activity</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatsCard title="Audit Events"     value={eventCounts.audit}   icon={ICONS.chart} />
          <StatsCard title="Call Events"      value={eventCounts.calls}   icon={ICONS.phone} />
          <StatsCard title="Video Call Events" value={eventCounts.video}  icon={ICONS.globe} />
          <StatsCard title="Visitor Sessions" value={eventCounts.visitor || '—'} icon={ICONS.people} />
          <StatsCard title="Access Events"    value={eventCounts.access}  icon={ICONS.shield} />
        </div>
      </section>

      {/* ── 6. Billable Events Log ───────────────────────────────────────── */}
      <BillableEventsLogSection />

    </div>
  );
};

// ── Billable Events Log — sourced from usage_ledger via /billing-events-log
//   PLUS AWS Cost Explorer per-service spend (real $) and allocated $/event.
const BillableEventsLogSection: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [aws, setAws] = useState<any>(null);
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/billing-events-log?limit=200`, { headers }).then(r => r.json()).then(setData).catch(() => setData({}));
    fetch(`${API}/aws-cost/summary`,            { headers }).then(r => r.json()).then(setAws).catch(() => setAws({}));
  }, []);

  // Format USD with enough precision to never display "$0.00" for a real
  // event — micro-dollars get 6 decimals, sub-cent gets 4, otherwise 2.
  const fmtUsd = (usd: any) => {
    const n = Number(usd || 0);
    if (!Number.isFinite(n)) return '$0.000000';
    if (n === 0) return '$0.000000';
    const abs = Math.abs(n);
    if (abs < 0.0001) return `$${n.toFixed(8)}`;
    if (abs < 0.01)   return `$${n.toFixed(6)}`;
    if (abs < 1)      return `$${n.toFixed(4)}`;
    return `$${n.toFixed(2)}`;
  };
  // Convert "cents" rows into USD then defer to fmtUsd so tiny amounts get
  // micro-precision instead of rounding to "$0.00".
  const fmtCents = (c: any) => fmtUsd(Number(c || 0) / 100);

  if (!data) return (
    <section>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Billable Events Log</h2>
      <Card><div className="p-6 text-center text-sm text-gray-400">Loading…</div></Card>
    </section>
  );

  const totals = data.totals || {};
  const byType: any[] = data.by_event_type || [];
  const recent: any[] = data.recent || [];
  const awsToday   = aws?.today  || { total_usd: 0, services: [] };
  const awsWeek    = aws?.week   || { total_usd: 0 };
  const awsMonth   = aws?.month  || { total_usd: 0 };
  const awsAlloc   = aws?.allocation || { events_today: 0, cost_per_event_usd: 0, cost_per_billable_usd: 0 };
  // Apportion AWS $/event across each ledger event type by share-of-volume
  // — total AWS cost (today) split proportionally by event_count.
  const totalEventsCount = byType.reduce((a, r) => a + Number(r.event_count || 0), 0);
  const awsCostShare = (count: number) =>
    totalEventsCount > 0 ? (awsToday.total_usd * (count / totalEventsCount)) : 0;

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        Billable Events Log
        <span className="ml-2 text-xs font-normal text-gray-400">
          sourced from usage_ledger · {Number(totals.events || 0).toLocaleString()} events · {Number(totals.event_types || 0)} types
        </span>
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatsCard title="Billable Events"     value={Number(totals.events || 0).toLocaleString()} icon={ICONS.activity} />
        <StatsCard title="Ledger Total"        value={fmtUsd(totals.total_cost_usd)}               icon={ICONS.revenue} change={`${Number(totals.configured_rates || 0)} rates configured`} />
        <StatsCard title="Unique People"       value={Number(totals.unique_people || 0).toLocaleString()} icon={ICONS.people} />
        <StatsCard title="Unique Properties"   value={Number(totals.unique_properties || 0).toLocaleString()} icon={ICONS.properties} />
      </div>

      {/* ── AWS Cost Explorer (real spend) ─────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatsCard title="AWS Cost (24h)"        value={fmtUsd(awsToday.total_usd)} icon={ICONS.server} />
        <StatsCard title="AWS Cost (7d)"         value={fmtUsd(awsWeek.total_usd)}  icon={ICONS.server} />
        <StatsCard title="AWS Cost (30d)"        value={fmtUsd(awsMonth.total_usd)} icon={ICONS.server} />
        <StatsCard title="AWS $/Event (24h)"     value={fmtUsd(awsAlloc.cost_per_event_usd)} icon={ICONS.chart}
          change={`${Number(awsAlloc.events_today || 0).toLocaleString()} events`} />
      </div>

      <Card padding={false} className="mb-4">
        <CardHeader title="AWS Cost by Service" subtitle="Real UnblendedCost from AWS Cost Explorer — last 24h" />
        <Table
          data={(awsToday.services || []).map((s: any, i: number) => ({ ...s, id: s.service || i }))}
          columns={[
            { key: 'service', header: 'AWS Service', render: (r: any) => <span className="text-sm font-medium text-gray-900">{r.service}</span> },
            { key: 'usd',     header: '24h Cost',    render: (r: any) => <span className="font-mono text-sm">{fmtUsd(r.usd)}</span> },
            { key: 'share',   header: 'Share',       render: (r: any) =>
              <span className="text-xs text-gray-500">{awsToday.total_usd > 0 ? `${(r.usd / awsToday.total_usd * 100).toFixed(2)}%` : '—'}</span>
            },
          ]}
        />
      </Card>

      <Card padding={false} className="mb-4">
        <CardHeader title="By Event Type" subtitle="Total cost, average cost, and per-instance cost from the service provider" />
        <Table
          data={byType.map((r: any, i: number) => ({ ...r, id: r.event_type || i }))}
          columns={[
            { key: 'event_type',   header: 'Event',          render: (r: any) => <span className="font-mono text-xs">{r.event_type}</span> },
            { key: 'event_count',  header: 'Count',          render: (r: any) => Number(r.event_count || 0).toLocaleString() },
            { key: 'provider',     header: 'Service',        render: (r: any) => (
              <span className="text-xs">{r.provider || '—'}
                {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-emerald-600 hover:underline">↗</a>}
              </span>
            )},
            { key: 'instance_cost_usd', header: 'Provider Cost / Event', render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.instance_cost_usd)}{r.unit_label ? ` / ${r.unit_label}` : ''}</span> },
            { key: 'avg_cost_usd',      header: 'Avg (ledger)',    render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.avg_cost_usd)}</span> },
            { key: 'total_cost_usd',    header: 'Total (ledger)',  render: (r: any) => <span className="font-mono text-xs font-semibold">{fmtUsd(r.total_cost_usd)}</span> },
            { key: 'aws_share',           header: 'AWS Cost Share (24h)', render: (r: any) => {
              const share = awsCostShare(Number(r.event_count || 0));
              const perEvent = Number(r.event_count) ? share / Number(r.event_count) : 0;
              return <span className="font-mono text-xs"><span className="font-semibold">{fmtUsd(share)}</span><span className="block text-[10px] text-gray-400">{fmtUsd(perEvent)} / event</span></span>;
            }},
          ]}
        />
      </Card>

      <Card padding={false}>
        <CardHeader title="Recent Events" subtitle="Most recent 200 billable events with who triggered each" />
        <Table
          data={recent.map((r: any) => ({ ...r, id: r.id }))}
          columns={[
            { key: 'occurred_at', header: 'When',     render: (r: any) => r.occurred_at ? new Date(r.occurred_at).toLocaleString() : '—' },
            { key: 'event_type',  header: 'Event',    render: (r: any) => <span className="font-mono text-xs">{r.event_type}</span> },
            { key: 'provider',    header: 'Service',  render: (r: any) => <span className="text-xs">{r.provider || '—'}</span> },
            { key: 'person',      header: 'Triggered By', render: (r: any) => (
              r.person_id
                ? <span className="text-xs"><span className="font-medium text-gray-900">{r.person_name || 'Unknown'}</span><span className="block font-mono text-[10px] text-gray-400">{r.person_id}</span></span>
                : <span className="text-xs text-gray-400">—</span>
            )},
            { key: 'property',    header: 'Property', render: (r: any) => r.property_name ? <span className="text-xs">{r.property_name}</span> : <span className="text-xs text-gray-400">—</span> },
            { key: 'quantity',    header: 'Qty',      render: (r: any) => <span className="text-xs">{r.quantity}{r.unit_label ? ` ${r.unit_label}` : ''}</span> },
            { key: 'unit_price_usd', header: 'Per-Instance', render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.unit_price_usd)}</span> },
            { key: 'row_cost_usd',   header: 'Cost',         render: (r: any) => <span className="font-mono text-xs font-semibold">{fmtUsd(r.row_cost_usd)}</span> },
          ]}
        />
      </Card>
    </section>
  );
};

// ── Website Analytics — per-surface card grid driven by /events/visitor/summary
const SURFACES: Array<{ host: string; label: string; subtitle: string }> = [
  { host: 'www.intrlock.io',     label: 'intrlock.io',         subtitle: 'Marketing Site' },
  { host: 'app.intrlock.io',     label: 'app.intrlock.io',     subtitle: 'Web App (Property Managers)' },
  { host: 'admin.intrlock.io',   label: 'admin.intrlock.io',   subtitle: 'Super-Admin Portal' },
  { host: 'tenants.intrlock.io', label: 'tenants.intrlock.io', subtitle: 'Tenant Portal' },
  { host: 'visitor.intrlock.io', label: 'visitor.intrlock.io', subtitle: 'Visitor Directories' },
];

const WebsiteAnalyticsSection: React.FC<{
  extra: { directoriesWithPhone: number; callCount: number; videoCallCount: number };
}> = ({ extra }) => {
  const [summary, setSummary] = useState<any>(null);
  const [apiTraffic, setApiTraffic] = useState<any>(null);
  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/events/visitor/summary`, { headers })
      .then(r => r.json()).then(setSummary).catch(() => {});
    fetch(`${API}/api-traffic/summary`, { headers })
      .then(r => r.json()).then(setApiTraffic).catch(() => {});
  }, []);

  const byDomain: Record<string, { views: number; sessions: number }> = {};
  for (const d of (summary?.by_domain || [])) byDomain[d.domain] = { views: d.views, sessions: d.sessions };

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        Website Analytics
        <span className="ml-2 text-xs font-normal text-gray-400">
          live · sourced from /events/visitor/summary + CloudWatch
        </span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SURFACES.map(s => {
          const d = byDomain[s.host] || { views: 0, sessions: 0 };
          const isVisitor = s.host === 'visitor.intrlock.io';
          return (
            <Card key={s.host}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{s.label}</h3>
                  <p className="text-xs text-gray-500">{s.subtitle}</p>
                </div>
                <Badge color={d.views > 0 ? 'green' : 'gray'}>{d.views > 0 ? 'Active' : 'Idle'}</Badge>
              </div>
              <div className="space-y-3">
                <StatRow label="Page Views"      value={d.views.toLocaleString()} />
                <StatRow label="Unique Sessions" value={d.sessions.toLocaleString()} />
                {isVisitor && (
                  <>
                    <StatRow label="Properties w/ Directory" value={extra.directoriesWithPhone} />
                    <StatRow label="Calls Initiated"         value={extra.callCount} />
                    <StatRow label="Video Calls"             value={extra.videoCallCount} />
                  </>
                )}
              </div>
            </Card>
          );
        })}

        {/* api.intrlock.io — sourced from CloudWatch */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">api.intrlock.io</h3>
              <p className="text-xs text-gray-500">Backend API · CloudWatch</p>
            </div>
            <Badge color={apiTraffic?.today?.requests > 0 ? 'green' : 'gray'}>
              {apiTraffic ? 'Live' : '—'}
            </Badge>
          </div>
          <div className="space-y-3">
            <StatRow label="Requests Today" value={apiTraffic ? Number(apiTraffic.today?.requests || 0).toLocaleString() : '—'} />
            <StatRow label="Requests 7d"    value={apiTraffic ? Number(apiTraffic.week?.requests  || 0).toLocaleString() : '—'} />
            <StatRow label="Requests 30d"   value={apiTraffic ? Number(apiTraffic.month?.requests || 0).toLocaleString() : '—'} />
            <StatRow label="4xx Errors (24h)" value={apiTraffic ? Number(apiTraffic.today?.errors_4xx || 0).toLocaleString() : '—'} />
            <StatRow label="5xx Errors (24h)" value={apiTraffic ? Number(apiTraffic.today?.errors_5xx || 0).toLocaleString() : '—'} />
            <StatRow label="Avg Latency"      value={apiTraffic ? `${apiTraffic.today?.latency_avg_ms || 0}ms` : '—'} />
          </div>
        </Card>
      </div>
    </section>
  );
};

// ── Page Analytics — every admin/app page-view across the portfolio ─────────
const PageAnalyticsSection: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || '';
      const res = await fetch(`${API}/events/visitor/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      setSummary(data);
      setRefreshedAt(new Date());
    } catch (e) {
      console.error('Failed to load page analytics:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh active-users every 30 s
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <Card><div className="p-8 text-center text-sm text-gray-400">Loading page analytics…</div></Card>;
  if (!summary || !summary.totals) return <Card><div className="p-8 text-center text-sm text-red-500">Page analytics unavailable — /events/visitor/summary returned no data.</div></Card>;

  const totals = summary.totals || {};
  const realtime = summary.realtime || {};
  const windows = summary.windows || {};
  const engagement = summary.engagement || {};
  const byDomain = summary.by_domain || [];
  const byDevice: Record<string, number> = summary.by_device || {};
  const byBrowser: Record<string, number> = summary.by_browser || {};
  const topPages = summary.top_pages || [];
  const recentViews = summary.recent_views || [];

  const domainRows = byDomain.map((d: any) => ({ ...d, id: d.domain }));
  const pathRows = topPages.map((p: any) => ({ ...p, id: p.path }));
  const recentRows = recentViews.map((r: any, i: number) => ({ ...r, id: `${r.when}-${i}` }));

  const topList = (m: Record<string, number>) =>
    Object.entries(m).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} (${v})`).join(' · ') || '—';

  return (
    <>
      <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-3">
        Page Analytics
        <span className="text-xs font-normal text-gray-400">
          live · {Number(totals.events || 0).toLocaleString()} events · refreshed {refreshedAt ? refreshedAt.toLocaleTimeString() : '—'}
        </span>
        <Button size="sm" variant="secondary" onClick={() => load()}>Refresh</Button>
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Active Users (5 min)"  value={`${realtime.active_users_5m || 0} (${realtime.active_sessions_5m || 0} sessions)`} icon={ICONS.activity} />
        <StatsCard title="Views Today"           value={Number(windows.views_today || 0).toLocaleString()}    icon={ICONS.chart} />
        <StatsCard title="Sessions Today"        value={Number(windows.sessions_today || 0).toLocaleString()} icon={ICONS.people} />
        <StatsCard title="Total Page Views"      value={Number(totals.page_views || 0).toLocaleString()}      icon={ICONS.globe} />
        <StatsCard title="Views 7d"              value={Number(windows.views_7d || 0).toLocaleString()}       icon={ICONS.chart} />
        <StatsCard title="Views 30d"             value={Number(windows.views_30d || 0).toLocaleString()}      icon={ICONS.chart} />
        <StatsCard title="Avg Dwell"             value={engagement.avg_dwell_seconds ? `${engagement.avg_dwell_seconds}s` : '—'} icon={ICONS.activity} />
        <StatsCard title="Bounce Rate"           value={`${engagement.bounce_rate ?? 0}%`}                    icon={ICONS.chart} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard title="Unique Sessions"       value={Number(totals.sessions || 0).toLocaleString()}        icon={ICONS.people} />
        <StatsCard title="Known Users"           value={Number(engagement.known_users || 0).toLocaleString()} icon={ICONS.people} />
        <StatsCard title="Devices · Browsers"    value={`${topList(byDevice)} | ${topList(byBrowser)}`}       icon={ICONS.mobile} />
      </div>

      <Card padding={false}>
        <CardHeader title="By Surface" subtitle="Page views split by hostname (matches per-surface GA4 IDs)" />
        <Table
          data={domainRows}
          columns={[
            { key: 'domain',   header: 'Hostname', render: (r: any) => <span className="font-mono text-xs">{r.domain}</span> },
            { key: 'views',    header: 'Views',    render: (r: any) => r.views.toLocaleString() },
            { key: 'sessions', header: 'Sessions', render: (r: any) => r.sessions.toLocaleString() },
          ]}
        />
      </Card>

      <Card padding={false}>
        <CardHeader title="Top Pages" subtitle="Views and avg. dwell time per route" />
        <Table
          data={pathRows}
          columns={[
            { key: 'path',         header: 'Path',         render: (r: any) => (
              <div>
                <span className="font-mono text-xs">{r.path}</span>
                {r.label && <span className="block text-[11px] text-emerald-700 font-medium">{r.label}</span>}
              </div>
            )},
            { key: 'views',        header: 'Views',        render: (r: any) => r.views.toLocaleString() },
            { key: 'sessions',     header: 'Sessions',     render: (r: any) => r.sessions.toLocaleString() },
            { key: 'avg_duration', header: 'Avg Dwell',    render: (r: any) => r.avg_duration ? `${r.avg_duration}s` : '—' },
          ]}
        />
      </Card>

      <Card padding={false}>
        <CardHeader title="Recent Visits" subtitle="Most recent 25 page-view events with device + browser" />
        <Table
          data={recentRows}
          columns={[
            { key: 'when',    header: 'When',    render: (r: any) => r.when ? new Date(r.when).toLocaleString() : '—' },
            { key: 'domain',  header: 'Domain',  render: (r: any) => <span className="text-xs text-gray-600">{r.domain}</span> },
            { key: 'path',    header: 'Path',    render: (r: any) => (
              <div className="max-w-[260px]">
                <span className="font-mono text-xs truncate block">{r.path}</span>
                {r.label && <span className="block text-[11px] text-emerald-700 font-medium truncate">{r.label}</span>}
              </div>
            )},
            { key: 'device',  header: 'Device',  render: (r: any) => <span className="text-xs">{r.device}</span> },
            { key: 'browser', header: 'Browser', render: (r: any) => <span className="text-xs">{r.browser}</span> },
          ]}
        />
      </Card>
    </>
  );
};

// ── Helper sub-component ──────────────────────────────────────────────────────
const StatRow: React.FC<{ label: string; value: string | number; note?: string }> = ({ label, value, note }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-500">{label}</span>
    <div className="text-right">
      <span className={`text-sm font-semibold ${value === '—' ? 'text-gray-300' : 'text-gray-900'}`}>{value}</span>
      {note && <span className="block text-xs text-gray-300">{note}</span>}
    </div>
  </div>
);
